import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
    setActiveFeature,
    setActiveProject,
    setAriaLiveRegionContents,
    setBasemap,
} from "../../actions/ui";
import {
    selectActiveFeature,
    selectSearchedLocation,
    selectFilteredProjects,
    selectIsFramed,
    selectIsMultiple,
    selectGeoJSON,
    selectBasemap,
    selectActiveMultipleProject,
    selectAllFundData,
    selectZoomToNsw,
    selectSettings,
} from "../../reducers/";
import "./map.css";
import { centerFromProject, projectShape } from "../../modules/project";
import pointInPolygon from "@turf/boolean-point-in-polygon";
import bboxPolygon from "@turf/bbox-polygon";
import bboxTurf from "@turf/bbox";
import turfPoint from "turf-point";
import styles from "./map.module.scss";
import Tooltip from "../tooltip/Tooltip";
import ReactMapGl, { SVGOverlay, FlyToInterpolator, Marker } from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import streetsMapStyle from "./streets-map-style.json";
import satelliteMapStyle from "./sattelite-map-style.json";
import "mapbox-gl/dist/mapbox-gl.css";
import DeckGL from "@deck.gl/react";
import { GeoJsonLayer } from "@deck.gl/layers";
import geojsonConfig from "../../modules/geojsonConfig";
import { feature as topoToGeo } from "topojson-client";
import Bowser from "bowser";
import ZoomNswButton from "../ui/map-buttons/ZoomNswButton";
import LayersButton from "../ui/map-buttons/LayersButton";
import MapNavigationControl from "./map-navigation-controls";
const bowser = Bowser.getParser(window.navigator.userAgent);
const browser = bowser.getBrowser();
const isIE11 = browser.version === "11.0" && browser.name === "Internet Explorer";

const geometryConfig = geojsonConfig["lga"];

const NSW_BBOX = [
    140.99927903600008, -37.50507657099996, 153.63883173500005, -28.157020554999974,
];

// Don't allow projects right on the edge of sight to be tabbable
// This causes the map to scroll when they're focused in Chromevox
const FOCUSABLE_PADDING = 10;

const KEY_ENTER = 13;
const KEY_SPACE = 32;

const ICON_HEIGHT = 32;
const ICON_WIDTH = 24;
const ICON_HEIGHT_MULTIPLE = 48;
const ICON_WIDTH_MULTIPLE = 32;
const CIRCLE_RADIUS = 4;

// Colours
const SOLID_BLUE = [0, 38, 181];
const SATELLITE_LINE_COLOR = [229, 246, 230]; // NSW gov Success green-bg
const TRANSPARENT_BLUE = [0, 38, 181, 255 / 20]; // i.e. rgba(0,38,101,0.05);
const TRANSPARENT = [0, 0, 0, 1];

const createIsProjectInsideFeature = feature => {
    const featureBboxPoly = feature && bboxPolygon(bboxTurf(feature));

    return project => {
        if (!feature) return false;

        const point = turfPoint(centerFromProject(project));
        if (!pointInPolygon(point, featureBboxPoly)) {
            return false;
        }
        return pointInPolygon(point, feature);
    };
};

export const projectsInBounds = (bounds, projects) => {
    const [swCorner, neCorner] = bounds;
    const [minLon, minLat] = swCorner;
    const [maxLon, maxLat] = neCorner;

    const projectsInBoundsMap = projects.reduce((acc, p) => {
        if (
            p.displayLong > minLon &&
            p.displayLong < maxLon &&
            p.displayLat > minLat &&
            p.displayLat < maxLat
        ) {
            return {
                ...acc,
                [p.applicationNo]: true,
            };
        } else {
            return acc;
        }
    }, {});

    return projectsInBoundsMap;
};

const flyTo = {
    transitionDuration: 500,
    transitionInterpolator: new FlyToInterpolator(),
};

const Map = ({
    width,
    height,
    idSelector,
    activeFeature,
    searchedLocation,
    isMobile,
    setActiveFeature,
    isMultiple,
    basemap,
    setBasemap,
    projects,
    setActiveProject,
    fundData,
    activeMultipleProject,
    zoomToNsw,
    settings,
}) => {
    const [hoverIcon, setHoverIcon] = useState();
    const [hoverCircle, setHoverCircle] = useState();
    const [hoveredFeature, setHoveredFeature] = useState();

    const mapRef = useRef();

    // Get colours from settings
    const { roadBasemapDotColor, satelliteBasemapDotColor } = settings;

    const initialViewport = useMemo(() => {
        return {
            width: width - 16,
            height: height - 16,
        };
    }, [height, width]);

    const getBaseMap = () => {
        return basemap === "ROADMAP" ? streetsMapStyle : satelliteMapStyle;
    };

    // For a11y
    // Allow people to press enter / space to open up projects
    // Which they have tabbed to on the map
    function keyHandler(event, project) {
        switch (event.which) {
            case KEY_SPACE:
            case KEY_ENTER: {
                event.stopPropagation();
                setActiveProject(project);
                break;
            }
            default:
                break;
        }
        return true;
    }

    const createNewViewport = React.useCallback(
        viewState => {
            const hasBbox =
                activeFeature &&
                activeFeature.properties &&
                activeFeature.properties.bbox;

            const bbox = hasBbox
                ? [
                      activeFeature.properties.bbox.slice(0, 2),
                      activeFeature.properties.bbox.slice(2, 4),
                  ]
                : [NSW_BBOX.slice(0, 2), NSW_BBOX.slice(2, 4)];

            // const responsiveSidebarWidth = isMobile ? 0 : SIDEBAR_WIDTH;
            const newViewport = new WebMercatorViewport({
                ...initialViewport,
                ...viewState,
            }).fitBounds(bbox, {
                padding: {
                    top: 10,
                    bottom: 10,
                    left: 10,
                    right: 10,
                },
            });

            return newViewport;
        },
        [activeFeature, initialViewport]
    );

    const initialViewState = React.useMemo(() => {
        const initialViewState = createNewViewport({});

        return initialViewState;
    }, [height, width]);

    const [viewState, setViewState] = useState(initialViewState);

    // If the zoomToNsw magic number changes,
    // Zoom to NSW
    useEffect(() => {
        setViewState({
            ...initialViewState,
            transitionInterpolator: new FlyToInterpolator(),
            transitionDuration: 200,
        });
    }, [zoomToNsw, height, width]);

    // Zoom to new place on new active feature
    useEffect(() => {
        if (!activeFeature) {
            return;
        }
        const isUnincorporatedNsw =
            activeFeature.properties["LGA_NAME16"] === "Unincorporated NSW";

        //   E,S,W,N
        const customBbox = [144.71782934, -34.5492769, 140.7855865, -27.39509];

        // There's some issue with Unincorporated NSW, this is a quick fix
        const bbox = isUnincorporatedNsw ? customBbox : bboxTurf(activeFeature);

        const newViewState = new WebMercatorViewport(viewState).fitBounds(
            [bbox.slice(0, 2), bbox.slice(2, 4)],
            {
                padding: { top: 10, bottom: 10, left: 10, right: 10 },
            }
        );

        setViewState(viewState => {
            return {
                ...viewState,
                ...newViewState,
                transitionInterpolator: new FlyToInterpolator(),
                transitionDuration: 200,
            };
        });
    }, [activeFeature]);

    const basemapStyles = getBaseMap();

    const setActiveFeatureIfNeeded = feature => {
        if (!feature) {
            if (activeFeature) {
                setActiveFeature(null);
            }
            return;
        }
        if (idSelector(feature) !== idSelector(activeFeature)) {
            setActiveFeature(feature);
        }
    };

    // projectsInView is a map keyed by the applicationNo
    // of projects in view
    // We need this, to know if dot should be tabbable
    const [projectsInView, setProjectsInView] = useState(null);

    // When the viewport or projects change,
    // recalculate projects in view
    useEffect(() => {
        if (!viewState.latitude) {
            setProjectsInView(null);
        }

        if (!isMobile && viewState.latitude) {
            // WebMercatorProjectViewport
            const webMercatorProjectViewport = new WebMercatorViewport({
                ...viewState,
                height: height,
                width: width,
            });

            // Set showing projects based on bounds
            // We can't use getBounds() because it's not supported by viewport-mercator-project
            // And later versions (like @math.gl/web-mercator) cause minification errors
            // with the current build setup
            const topLeftCorner = webMercatorProjectViewport.unproject([
                FOCUSABLE_PADDING,
                height - FOCUSABLE_PADDING,
            ]);
            const bottomRightCorner = webMercatorProjectViewport.unproject([
                width - FOCUSABLE_PADDING,
                FOCUSABLE_PADDING,
            ]);
            const bounds = [topLeftCorner, bottomRightCorner];

            const viewableProjects = projectsInBounds(bounds, projects);
            setProjectsInView(viewableProjects);
        }
    }, [viewState, projects, height, width, isMobile]);

    // // useEffect(() => {
    if (document.getElementById("deckgl-overlay")) {
        document.getElementById("deckgl-overlay").addEventListener("focus", () => {
            document.getElementById("crown-lands-live-region").innerText =
                "Map. Tab or use arrow keys to interact.";
        });
    }

    var mapBoxCanvasArray = document.getElementsByClassName("mapboxgl-canvas");
    if (mapBoxCanvasArray) {
        if (mapBoxCanvasArray.length) {
            var mapBoxMap = mapBoxCanvasArray[0];
            mapBoxMap.setAttribute(
                "aria-label",
                "New South Wales crown lands funding map."
            );
        }
    }

    return (
        <div className={styles.mapFrame}>
            <div className={styles.mapWrapper}>
                <ReactMapGl
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                    }}
                    // tabIndex={-1}
                    onViewportChange={x => {
                        setViewState(currentViewState => ({
                            ...currentViewState,
                            ...x,
                        }));
                    }}
                    {...viewState}
                    mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
                    width={"100%"}
                    height={"100%"}
                    ref={mapRef}
                    mapStyle={{
                        ...basemapStyles,
                    }}
                >
                    <DeckGL
                        width={initialViewport.width}
                        height={initialViewport.height}
                        viewState={viewState}
                        controller={null}
                        getCursor={({ isDragging }) => {
                            if (hoveredFeature) {
                                return "pointer";
                            }
                            return isDragging ? "grabbing" : "grab";
                        }}
                        layers={[
                            new GeoJsonLayer({
                                id: "electorates",
                                data: geometryConfig.geojsonUrl,
                                dataTransform: data => {
                                    const geojson = topoToGeo(
                                        data,
                                        data.objects[geometryConfig.topologyObjectsName]
                                    );
                                    return geojson;
                                },
                                stroked: true,
                                lineWidthUnits: "pixels",
                                lineWidthMinPixels: 0.5,
                                // Increase line width if feature is hovered or active
                                getLineWidth: d => {
                                    if (
                                        idSelector(d) === idSelector(hoveredFeature) ||
                                        idSelector(d) === idSelector(activeFeature)
                                    ) {
                                        // lineWidthUnits: "pixels" doesn't seem to be working with this deck version
                                        // So reverse engineer the scale mentioned here:(
                                        // https://deck.gl/docs/developer-guide/coordinate-systems#comparison-to-standard-3d-graphics-concepts
                                        return 120000 / Math.pow(2, viewState.zoom);
                                    } else {
                                        return 1;
                                    }
                                },
                                getFillColor: d => {
                                    if (
                                        idSelector(d) === idSelector(hoveredFeature) ||
                                        idSelector(d) === idSelector(activeFeature)
                                    ) {
                                        return TRANSPARENT;
                                    } else {
                                        return TRANSPARENT_BLUE;
                                    }
                                },
                                // highlightColor: TRANSPARENT,
                                getLineColor:
                                    basemap === "ROADMAP"
                                        ? SOLID_BLUE
                                        : SATELLITE_LINE_COLOR,
                                pickable: true,
                                updateTriggers: {
                                    // Make sure the map updates when a feature is hovered/active
                                    // So we can redraw line widths and change transparency
                                    // But not on IE11 as it doesn't work
                                    getFillColor: isIE11
                                        ? []
                                        : [activeFeature, hoveredFeature],
                                    getLineWidth: [
                                        activeFeature,
                                        hoveredFeature,
                                        viewState.zoom,
                                    ],
                                    getLineColor: [basemap],
                                },
                                autoHighlight: false,

                                // activeFeature is set when you click or hover
                                onClick: d => {
                                    setActiveFeatureIfNeeded(d.object);
                                    setActiveProject(null);
                                },
                                onHover: ({ object }) => {
                                    setHoveredFeature(object);
                                },
                            }),
                        ]}
                    />
                    <SVGOverlay
                        redraw={({ project }) => {
                            const isProjectInsideFeature =
                                createIsProjectInsideFeature(activeFeature);

                            return projects.map((d, i) => {
                                if (d.Project === "Re-build Urunga Tidal Pool") {
                                    return null;
                                }
                                let [x, y] = project([
                                    parseFloat(d.displayLong),
                                    parseFloat(d.displayLat),
                                ]);
                                const region = activeFeature
                                    ? idSelector(activeFeature.properties)
                                    : "no region";
                                let iconDimensions = {
                                    width: ICON_WIDTH,
                                    height: ICON_HEIGHT,
                                };

                                const fundObj = fundData[d.fundName];
                                const fundIcon = fundObj ? fundObj.icon : "";

                                if (d["Multiple locations"] === "Y") {
                                    iconDimensions = {
                                        width: ICON_WIDTH_MULTIPLE,
                                        height: ICON_HEIGHT_MULTIPLE,
                                    };
                                }

                                const circleMarker = (
                                    <circle
                                        className={styles.circleMarker}
                                        onClick={() => setActiveProject(d)}
                                        onMouseEnter={() => setHoverCircle(d)}
                                        onMouseLeave={() => setHoverCircle(null)}
                                        onFocus={() => setHoverCircle(d)}
                                        onBlur={() => setHoverCircle(null)}
                                        onKeyPress={event => keyHandler(event, d)}
                                        key={"circle" + region + d.Project + i}
                                        data-key={"circle" + region + d.Project + i}
                                        cx={x}
                                        cy={y}
                                        r={CIRCLE_RADIUS}
                                        fill={
                                            basemap === "ROADMAP"
                                                ? roadBasemapDotColor
                                                : satelliteBasemapDotColor
                                        }
                                        tabIndex={
                                            // Unreadable code for speed.
                                            // Basically if there's an active feature, those projects will have icon pins
                                            // so we don't want to tab to non-icon pins
                                            // Otherwise, we need to make sure the project is in view, OR
                                            // Projects in view have not been set
                                            activeFeature
                                                ? -1
                                                : (projectsInView &&
                                                      projectsInView[d.applicationNo]) ||
                                                  !projectsInView
                                                ? 2
                                                : -1
                                        }
                                        aria-label={`${d["Reserve Name"]}. ${d.Project}`}
                                        role={"button"}
                                        strokeWidth={5}
                                        stroke={"transparent"}
                                    />
                                );

                                const imageMarker = (
                                    <image
                                        onClick={e => setActiveProject(d)}
                                        tabIndex={2}
                                        onMouseEnter={() => setHoverIcon(d)}
                                        onMouseLeave={() => setHoverIcon(null)}
                                        onFocus={() => setHoverIcon(d)}
                                        onBlur={() => setHoverIcon(null)}
                                        onKeyPress={event => keyHandler(event, d)}
                                        key={region + d.Project + i}
                                        className={styles.imageMarker}
                                        // data-hovered={
                                        //     console.log(d) ||
                                        //     (hoverIcon && idSelector(d) === idSelector(hoverIcon))
                                        // }
                                        xlinkHref={
                                            d["Multiple locations"] === "Y"
                                                ? "img-multiple" + fundIcon
                                                : fundIcon
                                        }
                                        height={iconDimensions.height}
                                        x={x}
                                        y={y}
                                        width={iconDimensions.width}
                                        data-key={"circle" + region + d.Project + i}
                                        style={{
                                            transform: `translate(-${
                                                iconDimensions.width / 2
                                            }px,-${iconDimensions.height}px)`,
                                        }}
                                        aria-label={`${d["Reserve Name"]}. ${d.Project}`}
                                        alt={`${d.reserveName}. ${d.Project}`}
                                        role={"button"}
                                    />
                                );

                                // if displaying multiple projects across LGAs, use image
                                // else if the project is inside the selected region, use image
                                // else use default circle

                                if (isMultiple) {
                                    return d.ID === activeMultipleProject.ID
                                        ? imageMarker
                                        : circleMarker;
                                } else {
                                    return isProjectInsideFeature(d)
                                        ? imageMarker
                                        : circleMarker;
                                }
                            });
                        }}
                    />
                    {searchedLocation && (
                        <Marker
                            latitude={searchedLocation[1]}
                            longitude={searchedLocation[0]}
                        >
                            <img
                                style={{
                                    transform: `translate(-50%, -50%)`,
                                }}
                                alt="Marker icon, a star"
                                src={`${process.env.PUBLIC_URL}/img/star.svg`}
                            />
                        </Marker>
                    )}
                    <Tooltip
                        item={hoverCircle || hoverIcon}
                        offsetTop={Boolean(hoverIcon) ? -ICON_HEIGHT : -CIRCLE_RADIUS}
                        titleSelector={d => d["Reserve Name"]}
                        nameSelector={d => d.Project}
                    />
                </ReactMapGl>
            </div>
            <MapNavigationControl
                onZoomInClick={() => {
                    setViewState({ ...viewState, zoom: viewState.zoom + 1, ...flyTo });
                }}
                onZoomOutClick={() => {
                    setViewState({ ...viewState, zoom: viewState.zoom - 1, ...flyTo });
                }}
            />
            {isMobile && (
                <React.Fragment>
                    <ZoomNswButton
                        onClick={() => {
                            setActiveFeature(null);
                        }}
                        ariaLabel={"Zoom to NSW"}
                        isMobile={true}
                    />
                    <LayersButton
                        onClick={() => {
                            const newBasemap =
                                basemap === "ROADMAP" ? "HYBRID" : "ROADMAP";
                            setBasemap(newBasemap);
                        }}
                    />
                </React.Fragment>
            )}
        </div>
    );
};

const featureShape = {
    type: PropTypes.string.isRequired,
    geometry: PropTypes.object.isRequired,
    properties: PropTypes.object.isRequired,
};
Map.propTypes = {
    idSelector: PropTypes.func.isRequired,
    projects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
    activeFeature: PropTypes.shape(featureShape),
    setActiveFeature: PropTypes.func.isRequired,
    setActiveProject: PropTypes.func.isRequired,
    setAriaLiveRegionContents: PropTypes.func.isRequired,
    setBasemap: PropTypes.func.isRequired,
    searchedLocation: PropTypes.arrayOf(PropTypes.number),
    isFramed: PropTypes.bool,
    isMultiple: PropTypes.bool,
    basemap: PropTypes.string.isRequired,
    idCol: PropTypes.string.isRequired,
    geojson: PropTypes.shape({
        type: PropTypes.string.isRequired,
        features: PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.string.isRequired,
                geometry: PropTypes.object.isRequired,
                properties: PropTypes.object.isRequired,
            }).isRequired
        ),
    }),
    activeMultipleProject: PropTypes.shape(projectShape),
    height: PropTypes.number,
    width: PropTypes.number,
    zoomToNsw: PropTypes.number.isRequired,
    fundData: PropTypes.object.isRequired,
    isMobile: PropTypes.bool.isRequired,
    settings: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    activeFeature: selectActiveFeature(state),
    zoomToNsw: selectZoomToNsw(state),
    searchedLocation: selectSearchedLocation(state),
    projects: selectFilteredProjects(state),
    isFramed: selectIsFramed(state),
    isMultiple: selectIsMultiple(state),
    geojson: selectGeoJSON(state),
    basemap: selectBasemap(state),
    activeMultipleProject: selectActiveMultipleProject(state),
    settings: selectSettings(state),
    fundData: selectAllFundData(state),
});
const mapDispatchToProps = {
    setActiveFeature,
    setActiveProject,
    setBasemap,
    setAriaLiveRegionContents,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js